import { useTranslations } from '@/lib/next-shim'
import { BoltIcon } from '@heroicons/react/20/solid'

import { AvailabilityMeetingPlace } from '@/core/graphql/types'

import { MeetingPlace } from './MeetingPlace'
import { MeetingLink } from '../api/getMeetingLink'
import { BookApppointmentButton } from './BookAppointmentButton'
import { ConsultNowButton } from './ConsultNowButton'

type SummaryProps = {
  meetingLink: MeetingLink
  meetingLinkSlug: string
}

export function Summary({ meetingLink, meetingLinkSlug }: SummaryProps) {
  const t = useTranslations()

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
        <h2 className="text-xl font-medium text-gray-900">{t('page.MeetingLink.summarySection.title')}</h2>
      </div>
      <div className="flex flex-col gap-6 px-4 py-5 sm:p-6">
        <ConsultationOptions availabilities={meetingLink.availabilities} />
      </div>
      <CTAButtons meetingLink={meetingLink} meetingLinkSlug={meetingLinkSlug} />
    </div>
  )
}

function ConsultationOptions({ availabilities }: { availabilities: MeetingLink['availabilities'] }) {
  const t = useTranslations()

  const availabilitiesByType = availabilities.reduce(
    (acc, availability) => {
      if (!acc[availability.meetingPlace]) {
        acc[availability.meetingPlace] = []
      }
      acc[availability.meetingPlace].push(availability)
      return acc
    },
    {} as Record<AvailabilityMeetingPlace, typeof availabilities>
  )

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">{t('page.MeetingLink.meetingPlaceSection.title')}</h3>
      <div className="space-y-4">
        {Object.entries(availabilitiesByType).map(([meetingPlace, availabilities]) => (
          <MeetingPlace key={meetingPlace} availabilities={availabilities} meetingPlace={meetingPlace as AvailabilityMeetingPlace} />
        ))}
      </div>
    </div>
  )
}

function CTAButtons({ meetingLink, meetingLinkSlug }: { meetingLink: MeetingLink; meetingLinkSlug: string }) {
  const isImmediateEnabled = meetingLink?.immediateConsultationConfig?.enabled && !!meetingLink?.waitingRoom

  return (
    <div className="hidden sm:flex flex-col gap-3 px-4 py-5 border-t border-gray-200 sm:px-6">
      {isImmediateEnabled && (
        <ConsultNowButton
          className="w-full px-4 font-semibold text-primary border-2 border-primary hover:bg-primary-50 transition-all flex items-center justify-center gap-2"
          meetingLinkSlug={meetingLinkSlug}
          size="large"
        >
          <BoltIcon className="h-5 w-5" />
        </ConsultNowButton>
      )}
      <BookApppointmentButton className="w-full px-4 font-medium text-white bg-primary hover:bg-primary-dark" meetingLinkSlug={meetingLinkSlug} size="large" />
    </div>
  )
}
